import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notasFiscaisApi, contasApi, categoriasApi, cartoesApi, comprasDivididasApi } from '../../api/endpoints'
import { Button, IconButton, Spinner } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, AvatarIcon } from '../../components/ui/Card'
import { Badge, DonoBadge } from '../../components/ui/Badge'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Tabs } from '../../components/ui/Navigation'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { QrScanner, lerQrDaImagem } from '../../components/QrScanner'
import { PageHeader } from '../../layout/PageHeader'
import { PartesDivisao } from '../transacoes/DivisaoCartoes'
import { partesVazias, validarPartes, valoresDasPartes, type ParteForm } from '../transacoes/divisao'
import { AbastecimentoDaNota } from '../veiculos/AbastecimentoDaNota'
import { InsightsNota } from './InsightsNota'
import { AJUDA_RECORRENCIA, CategoriasItens, CompraSemNotaModal, EstoqueModal, PlanoCompraModal } from './GestaoItens'
import { OrigemNota, ORIGEM_NOTA_LABEL, StatusNota, PRAZO_CONSULTA_NOTA_DIAS, ModalidadeCartao } from '../../lib/enums'
import { chaveValida, formatarChave, lerConteudoQr, somenteDigitos, interpretarTextoCupom } from '../../lib/nfe'
import { formatMoney, formatDate, cn } from '../../lib/utils'
import type { CatalogoItens, ItemCompradoGrupo, NotaFiscal, NotaItem, NotaItemEntrada, ResumoCategoria, TipoMarcaRecorrencia } from '../../types'

type Metodo = 'qrcode' | 'imagem' | 'chave' | 'foto'

const METODOS = [
  { key: 'qrcode' as const, label: 'Escanear QR', icon: 'ri-qr-scan-2-line' },
  { key: 'imagem' as const, label: 'Imagem com QR', icon: 'ri-image-line' },
  { key: 'chave' as const, label: 'Digitar chave', icon: 'ri-keyboard-line' },
  { key: 'foto' as const, label: 'Foto do cupom', icon: 'ri-camera-lens-line' },
]

const ORIGEM_VARIANT: Record<number, 'primary' | 'info' | 'warning' | 'muted'> = {
  [OrigemNota.QrCode]: 'primary',
  [OrigemNota.Ocr]: 'warning',
  [OrigemNota.Manual]: 'muted',
  [OrigemNota.Sefaz]: 'info',
}

interface NotaForm {
  chave: string
  urlConsulta: string
  nomeEmitente: string
  cnpjEmitente: string
  dataEmissao: string
  valorTotal: string
  origem: OrigemNota
  accountId: string
  categoriaId: string
  descricao: string
  itens: NotaItemEntrada[]
}

function formVazio(): NotaForm {
  return {
    chave: '', urlConsulta: '', nomeEmitente: '', cnpjEmitente: '',
    dataEmissao: new Date().toISOString().slice(0, 10), valorTotal: '',
    origem: OrigemNota.Manual, accountId: '', categoriaId: '', descricao: '', itens: [],
  }
}

export function NotasFiscaisPage() {
  const qc = useQueryClient()
  const toast = useToast()

  const [modalOpen, setModalOpen] = useState(false)
  const [metodo, setMetodo] = useState<Metodo>('qrcode')
  const [form, setForm] = useState<NotaForm>(formVazio())
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [processando, setProcessando] = useState<string | null>(null)
  const [detalheId, setDetalheId] = useState<string | null>(null)
  const [aba, setAba] = useState<'notas' | 'itens' | 'resumo' | 'categorias'>('notas')
  const [compraSemNota, setCompraSemNota] = useState(false)
  const [parametros, setParametros] = useSearchParams()
  const [paraExcluir, setParaExcluir] = useState<NotaFiscal | null>(null)
  const inputImagem = useRef<HTMLInputElement>(null)
  const inputFoto = useRef<HTMLInputElement>(null)

  const { data: page, isLoading } = useQuery({ queryKey: ['notas-fiscais'], queryFn: () => notasFiscaisApi.list() })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: modalOpen })
  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
    enabled: modalOpen,
  })

  const criar = useMutation({
    mutationFn: () => notasFiscaisApi.criar({
      chave: form.chave ? somenteDigitos(form.chave) : undefined,
      urlConsulta: form.urlConsulta || undefined,
      cnpjEmitente: form.cnpjEmitente || undefined,
      nomeEmitente: form.nomeEmitente || undefined,
      dataEmissao: form.dataEmissao,
      valorTotal: Number(form.valorTotal),
      origem: form.origem,
      itens: form.itens.length ? form.itens : undefined,
      accountId: form.accountId || undefined,
      categoriaId: form.categoriaId || undefined,
      descricao: form.descricao || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notas-fiscais'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(form.accountId ? 'Nota importada e lançamento criado.' : 'Nota importada.')
      setModalOpen(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  // Nota de MG pelo Web: o navegador não lê a SEFAZ, então salva como Pendente; os itens vêm do .html salvo.
  const pendenteMg = useMutation({
    mutationFn: (chave: string) => notasFiscaisApi.importarHtml({ chave, urlConsulta: form.urlConsulta || undefined }),
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ['notas-fiscais'] })
      toast.success(n.status === StatusNota.Pendente ? 'Nota salva como pendente. Envie a página salva (.html) para importar os itens.' : 'Esta nota já estava importada.')
      setModalOpen(false)
      setDetalheId(n.id)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => notasFiscaisApi.remover(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notas-fiscais'] })
      toast.success('Nota removida.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function abrirImportacao() {
    setForm(formVazio())
    setMetodo('qrcode')
    setErro(null)
    setAviso(null)
    setModalOpen(true)
  }

  function aplicarConteudoQr(conteudo: string) {
    const dados = lerConteudoQr(conteudo)
    if (!dados) {
      setErro('Não reconheci uma NFC-e nesse QR Code.')
      return
    }

    setErro(null)
    setAviso(dados.valorTotal ? null : 'O QR Code trouxe só a chave. Confira o valor e a data no cupom.')
    setForm((f) => ({
      ...f,
      chave: dados.chave,
      urlConsulta: dados.url ?? '',
      origem: OrigemNota.QrCode,
      valorTotal: dados.valorTotal ? String(dados.valorTotal) : f.valorTotal,
      dataEmissao: dados.dataEmissao ? dados.dataEmissao.slice(0, 10) : f.dataEmissao,
    }))
    setMetodo('chave')
  }

  async function lerImagem(arquivo: File) {
    setProcessando('Procurando o QR Code na imagem...')
    setErro(null)
    try {
      const conteudo = await lerQrDaImagem(arquivo)
      if (!conteudo) {
        setErro('Não encontrei um QR Code nessa imagem. Tente a opção "Foto do cupom".')
        return
      }
      aplicarConteudoQr(conteudo)
    } finally {
      setProcessando(null)
    }
  }

  async function lerFotoComOcr(arquivo: File) {
    setErro(null)
    setAviso(null)

    setProcessando('Procurando QR Code na foto...')
    try {
      const conteudo = await lerQrDaImagem(arquivo)
      if (conteudo) {
        aplicarConteudoQr(conteudo)
        return
      }
    } catch {
      // segue para o OCR
    }

    setProcessando('Lendo o texto do cupom... isso pode demorar um pouco na primeira vez.')
    try {
      const { default: Tesseract } = await import('tesseract.js')
      const { data } = await Tesseract.recognize(arquivo, 'por')
      const lido = interpretarTextoCupom(data.text)

      const achou = [
        lido.chave && 'chave',
        lido.valorTotal && 'valor',
        lido.itens.length > 0 && `${lido.itens.length} itens`,
      ].filter(Boolean)

      if (achou.length === 0) {
        setErro('Não consegui ler o cupom. Preencha os dados à mão ou tente outra foto, mais reta e com o cupom inteiro no quadro.')
      } else {
        setAviso(`Li ${achou.join(', ')} do cupom. Confira tudo antes de salvar: leitura por foto erra com frequência.`)
      }

      setForm((f) => ({
        ...f,
        origem: lido.chave ? OrigemNota.QrCode : OrigemNota.Ocr,
        chave: lido.chave ?? f.chave,
        cnpjEmitente: lido.cnpj ?? f.cnpjEmitente,
        nomeEmitente: f.nomeEmitente || lido.emitente || '',
        valorTotal: lido.valorTotal ? String(lido.valorTotal) : f.valorTotal,
        dataEmissao: lido.dataEmissao ?? f.dataEmissao,
        itens: lido.itens.length > 0 ? lido.itens.map((i) => ({
          descricao: i.descricao,
          codigo: i.codigo,
          quantidade: i.quantidade,
          unidade: i.unidade,
          valorUnitario: i.valorUnitario,
          valorTotal: i.valorTotal,
        })) : f.itens,
      }))
      setMetodo('chave')
    } catch {
      setErro('Falha ao processar a imagem.')
    } finally {
      setProcessando(null)
    }
  }

  function aplicarChaveDigitada() {
    const digitos = somenteDigitos(form.chave)
    if (digitos.length !== 44) return setErro('A chave precisa ter 44 dígitos.')
    if (!chaveValida(digitos)) return setErro('Chave inválida: o dígito verificador não confere.')
    setErro(null)
    setAviso('Chave válida. Complete o valor e a data do cupom.')
    setForm((f) => ({ ...f, chave: digitos, origem: f.origem === OrigemNota.Manual ? OrigemNota.Manual : f.origem }))
  }

  function alterarItem(indice: number, campo: keyof NotaItemEntrada, valor: string) {
    setForm((f) => {
      const itens = [...f.itens]
      const item = { ...itens[indice] }

      if (campo === 'descricao' || campo === 'unidade' || campo === 'codigo') {
        item[campo] = valor
      } else {
        const numero = Number(valor) || 0
        if (campo === 'quantidade') item.quantidade = numero
        if (campo === 'valorUnitario') item.valorUnitario = numero
        if (campo === 'valorTotal') item.valorTotal = numero
      }

      if (campo === 'quantidade' || campo === 'valorUnitario') {
        item.valorTotal = Number((item.quantidade * item.valorUnitario).toFixed(2))
      }

      itens[indice] = item
      return { ...f, itens }
    })
  }

  function submit() {
    const chaveDigitos = somenteDigitos(form.chave)
    if (chaveDigitos.length === 44 && chaveDigitos.startsWith('31') && form.itens.length === 0 && !form.accountId) {
      pendenteMg.mutate(chaveDigitos)
      return
    }
    if (!form.valorTotal || Number(form.valorTotal) <= 0) {
      setErro('Informe o valor total da nota.')
      return
    }
    criar.mutate()
  }

  const notas = page?.itens ?? []
  const detalhe = notas.find((n) => n.id === detalheId) ?? null

  // Vindo do selo "Nota fiscal" de um lançamento: /notas-fiscais?nota=<id> abre a compra.
  useEffect(() => {
    const id = parametros.get('nota')
    if (!id) return
    setDetalheId(id)
    setParametros({}, { replace: true })
  }, [parametros, setParametros])
  const somaItens = form.itens.reduce((s, i) => s + i.valorTotal, 0)

  return (
    <>
      <PageHeader title="Notas fiscais" />
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Link to="/grupos-itens" className="inline-flex h-[38px] items-center gap-2 rounded bg-surface2 px-4 text-13 font-medium text-white hover:bg-surface3"><i className="ri-stack-line text-base" aria-hidden /> Grupos de itens</Link>
        <Link to="/itens-sem-grupo" className="inline-flex h-[38px] items-center gap-2 rounded bg-surface2 px-4 text-13 font-medium text-white hover:bg-surface3"><i className="ri-question-line text-base" aria-hidden /> Itens sem grupo</Link>
        <Button variant="light" icon="ri-shopping-basket-line" onClick={() => setCompraSemNota(true)}>Compra sem nota</Button>
      </div>
      <CompraSemNotaModal open={compraSemNota} onClose={() => setCompraSemNota(false)} />

      <Tabs
        tabs={[{ key: 'notas' as const, label: 'Notas', icon: 'ri-receipt-line' }, { key: 'itens' as const, label: 'Itens comprados', icon: 'ri-price-tag-3-line' }, { key: 'resumo' as const, label: 'Resumo', icon: 'ri-bar-chart-2-line' }, { key: 'categorias' as const, label: 'Categorias', icon: 'ri-links-line' }]}
        active={aba} onChange={setAba} className="mb-4"
      />

      {aba === 'itens' && <ItensComprados onAbrirNota={(id) => setDetalheId(id)} />}
      {aba === 'resumo' && <ResumoItensWeb />}
      {aba === 'categorias' && <CategoriasItens />}

      <Card className={aba !== 'notas' ? 'hidden' : ''}>
        <CardHeader
          title="Notas importadas"
          icon="ri-receipt-line"
          actions={<Button data-tutorial="nota.escanear" icon="ri-qr-scan-2-line" onClick={abrirImportacao}>Importar nota</Button>}
        />
        {isLoading ? (
          <LoadingState />
        ) : notas.length === 0 ? (
          <EmptyState
            icon="ri-receipt-line"
            message="Nenhuma nota importada ainda."
            action={<Button variant="soft-primary" icon="ri-qr-scan-2-line" onClick={abrirImportacao}>Importar a primeira</Button>}
          />
        ) : (
          <Table>
            <THead>
              <tr><Th>Emitente</Th><Th>Data</Th><Th>Origem</Th><Th>Itens</Th><Th>Lançamento</Th><Th className="text-right">Valor</Th><Th className="text-right">Ações</Th></tr>
            </THead>
            <tbody>
              {notas.map((n) => (
                <Tr key={n.id}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <AvatarIcon icon="ri-store-2-line" tone="primary" size="sm" />
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium text-white">
                          <span className="truncate">{n.apelido ?? n.nomeEmitente ?? 'Emitente não informado'}</span>
                          {n.status === StatusNota.Pendente && <Badge variant="warning">Pendente</Badge>}
                          <DonoBadge nome={n.ownerNome} />
                        </p>
                        {n.chave && <p className="truncate font-mono text-2xs text-muted">{formatarChave(n.chave)}</p>}
                      </div>
                    </div>
                  </Td>
                  <Td className="whitespace-nowrap text-muted">{formatDate(n.dataEmissao)}</Td>
                  <Td><Badge variant={ORIGEM_VARIANT[n.origem] ?? 'muted'}>{ORIGEM_NOTA_LABEL[n.origem]}</Badge></Td>
                  <Td className="text-muted">{n.itens.length || '—'}{n.valorItens != null && n.valorItens - n.valorTotal > 0.004 && <span className="block text-2xs text-income">desc. {formatMoney(n.valorItens - n.valorTotal)}</span>}</Td>
                  <Td className="text-muted">{n.transacaoDescricao ?? '—'}</Td>
                  <Td className="whitespace-nowrap text-right font-semibold text-expense">{n.status === StatusNota.Pendente ? '—' : formatMoney(n.valorTotal)}</Td>
                  <Td className="whitespace-nowrap text-right">
                    <IconButton icon="ri-eye-fill" label={`Ver nota de ${n.nomeEmitente ?? 'emitente'}`} tone="info" onClick={() => setDetalheId(n.id)} />
                    <IconButton icon="ri-delete-bin-5-fill" label="Excluir nota" tone="danger" onClick={() => setParaExcluir(n)} />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Importar nota fiscal"
        size="lg"
        footer={
          <>
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button icon="ri-save-line" onClick={submit} loading={criar.isPending}>Salvar nota</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Tabs tabs={METODOS} active={metodo} onChange={(k) => { setMetodo(k); setErro(null) }} />

          {metodo === 'qrcode' && <QrScanner onDetect={aplicarConteudoQr} />}

          {metodo === 'imagem' && (
            <div className="rounded border border-dashed border-border p-6 text-center">
              <i className="ri-image-add-line text-3xl text-muted" aria-hidden />
              <p className="mt-2 text-13 text-muted">Escolha uma imagem que contenha o QR Code do cupom.</p>
              <input ref={inputImagem} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && lerImagem(e.target.files[0])} />
              <Button variant="soft-primary" icon="ri-upload-2-line" className="mt-3" onClick={() => inputImagem.current?.click()}>Escolher imagem</Button>
            </div>
          )}

          {metodo === 'foto' && (
            <div className="rounded border border-dashed border-border p-6 text-center">
              <i className="ri-camera-lens-line text-3xl text-muted" aria-hidden />
              <p className="mt-2 text-13 text-muted">Foto do cupom inteiro. Tento achar o QR Code e, se não houver, leio o texto.</p>
              <p className="mt-1 text-2xs text-muted">No celular abre a câmera. A leitura de texto erra bastante: confira os valores.</p>
              <input ref={inputFoto} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files?.[0] && lerFotoComOcr(e.target.files[0])} />
              <Button variant="soft-primary" icon="ri-camera-line" className="mt-3" onClick={() => inputFoto.current?.click()}>Tirar ou escolher foto</Button>
            </div>
          )}

          {metodo === 'chave' && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[260px] flex-1">
                <Input
                  label="Chave de acesso (44 dígitos)"
                  value={form.chave}
                  onChange={(e) => setForm((f) => ({ ...f, chave: e.target.value }))}
                  placeholder="0000 0000 0000 ..."
                  inputMode="numeric"
                  className="font-mono"
                />
              </div>
              <Button variant="soft-info" icon="ri-check-line" onClick={aplicarChaveDigitada}>Validar</Button>
            </div>
          )}

          {processando && (
            <p className="flex items-center gap-2 rounded bg-surface2 px-3 py-2.5 text-13 text-muted">
              <Spinner className="h-4 w-4 text-primary" /> {processando}
            </p>
          )}
          {aviso && <p className="flex items-center gap-1.5 rounded bg-warning/10 px-3 py-2 text-13 text-warning"><i className="ri-information-line" aria-hidden /> {aviso}</p>}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}

          <div className="grid gap-4 border-t border-border/60 pt-4 sm:grid-cols-2">
            <Input label="Emitente" value={form.nomeEmitente} onChange={(e) => setForm((f) => ({ ...f, nomeEmitente: e.target.value }))} placeholder="Ex: Supermercado Bom Preço" />
            <Input label="CNPJ" value={form.cnpjEmitente} onChange={(e) => setForm((f) => ({ ...f, cnpjEmitente: e.target.value }))} placeholder="Somente números" inputMode="numeric" />
            <Input label="Data da compra" type="date" value={form.dataEmissao} onChange={(e) => setForm((f) => ({ ...f, dataEmissao: e.target.value }))} />
            <Input label="Valor total (R$)" type="number" min={0} step="0.01" value={form.valorTotal} onChange={(e) => setForm((f) => ({ ...f, valorTotal: e.target.value }))} />
          </div>

          <div className="rounded border border-border/60 p-3">
            <p className="mb-2 text-13 font-medium text-white">Criar lançamento junto (opcional)</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <Select label="Conta" value={form.accountId} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}>
                <option value="">Não criar lançamento</option>
                {contas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              <Select label="Categoria" value={form.categoriaId} disabled={!form.accountId} onChange={(e) => setForm((f) => ({ ...f, categoriaId: e.target.value }))}>
                <option value="">Sem categoria</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
              </Select>
              <Input label="Descrição" value={form.descricao} disabled={!form.accountId} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} placeholder="Compra no mercado" />
            </div>
          </div>

          <div className="rounded border border-border/60">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
              <p className="text-13 font-medium text-white">Itens da nota <span className="text-muted">({form.itens.length})</span></p>
              <div className="flex items-center gap-3">
                {form.itens.length > 0 && (
                  <span className={cn('text-2xs', Math.abs(somaItens - Number(form.valorTotal || 0)) < 0.01 ? 'text-income' : 'text-warning')}>
                    Soma dos itens: {formatMoney(somaItens)}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="soft-primary"
                  icon="ri-add-line"
                  onClick={() => setForm((f) => ({ ...f, itens: [...f.itens, { descricao: '', quantidade: 1, valorUnitario: 0, valorTotal: 0 }] }))}
                >
                  Adicionar item
                </Button>
              </div>
            </div>

            {form.itens.length === 0 ? (
              <p className="px-3 py-4 text-center text-13 text-muted">Sem itens. Útil para compras de supermercado, e serve de base para o planejamento por produto.</p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {form.itens.map((item, i) => (
                  <div key={i} className="grid grid-cols-[1fr_70px_90px_90px_32px] items-center gap-2 border-b border-border/40 px-3 py-2 last:border-b-0">
                    <input className="form-control h-8" value={item.descricao} placeholder="Descrição" onChange={(e) => alterarItem(i, 'descricao', e.target.value)} aria-label={`Descrição do item ${i + 1}`} />
                    <input className="form-control h-8" type="number" min={0} step="0.001" value={item.quantidade} onChange={(e) => alterarItem(i, 'quantidade', e.target.value)} aria-label={`Quantidade do item ${i + 1}`} />
                    <input className="form-control h-8" type="number" min={0} step="0.01" value={item.valorUnitario} onChange={(e) => alterarItem(i, 'valorUnitario', e.target.value)} aria-label={`Valor unitário do item ${i + 1}`} />
                    <input className="form-control h-8" type="number" min={0} step="0.01" value={item.valorTotal} onChange={(e) => alterarItem(i, 'valorTotal', e.target.value)} aria-label={`Valor total do item ${i + 1}`} />
                    <IconButton icon="ri-close-line" label={`Remover item ${i + 1}`} tone="danger" onClick={() => setForm((f) => ({ ...f, itens: f.itens.filter((_, idx) => idx !== i) }))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal open={!!detalhe} onClose={() => setDetalheId(null)} title={detalhe ? (detalhe.apelido ?? detalhe.nomeEmitente ?? 'Nota fiscal') : 'Nota fiscal'} size="lg">
        {detalhe && <DetalheNota nota={detalhe} />}
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        loading={remover.isPending}
        message={<>A nota de <strong className="text-white">{paraExcluir?.nomeEmitente ?? 'emitente não informado'}</strong> será removida. O lançamento criado a partir dela continua.</>}
      />
    </>
  )
}

function DetalheNota({ nota }: { nota: NotaFiscal }) {
  const qc = useQueryClient()
  const toast = useToast()
  const inputHtml = useRef<HTMLInputElement>(null)
  const [busca, setBusca] = useState('')
  const [apelido, setApelido] = useState(nota.apelido ?? '')
  const [criando, setCriando] = useState(false)
  const [contaId, setContaId] = useState('')
  const [pagoCom, setPagoCom] = useState<'conta' | 'cartao'>('conta')
  const [cartaoId, setCartaoId] = useState('')
  const [parcelas, setParcelas] = useState('1')
  const [debito, setDebito] = useState(false)
  const [dividir, setDividir] = useState(false)
  const [partes, setPartes] = useState<ParteForm[]>(partesVazias())
  const [categoriaId, setCategoriaId] = useState('')

  const pendente = nota.status === StatusNota.Pendente
  const semLancamento = !pendente && !nota.transacaoId && nota.valorTotal > 0
  const desconto = nota.valorItens != null ? nota.valorItens - nota.valorTotal : 0
  const limite = new Date(new Date(nota.dataEmissao).getTime() + PRAZO_CONSULTA_NOTA_DIAS * 86_400_000)

  const candidatos = useQuery({ queryKey: ['nota-candidatos', nota.id], queryFn: () => notasFiscaisApi.lancamentosCandidatos(nota.id), enabled: semLancamento })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: criando })
  const { data: cartoes = [] } = useQuery({ queryKey: ['cartoes'], queryFn: cartoesApi.list, enabled: criando })
  const { data: cartoesUtilizaveis = [] } = useQuery({ queryKey: ['cartoes-utilizaveis'], queryFn: comprasDivididasApi.cartoesUtilizaveis, enabled: criando && pagoCom === 'cartao' && dividir })
  const dividindo = pagoCom === 'cartao' && dividir
  const erroDivisao = dividindo ? validarPartes(nota.valorTotal, partes) : null
  const cartaoEscolhido = cartoes.find((c) => c.id === cartaoId)
  const cartaoComDebito = !!cartaoEscolhido && cartaoEscolhido.modalidade === ModalidadeCartao.Credito && cartaoEscolhido.temDebito
  const creditoParcelavel = pagoCom === 'cartao' && !!cartaoEscolhido && cartaoEscolhido.modalidade === ModalidadeCartao.Credito && !(cartaoComDebito && debito)
  const parcelado = !dividindo && creditoParcelavel && (Math.floor(Number(parcelas)) || 1) > 1
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: () => categoriasApi.list({ incluirInativas: false }), enabled: criando })

  const depois = () => {
    qc.invalidateQueries({ queryKey: ['notas-fiscais'] })
    qc.invalidateQueries({ queryKey: ['nota-candidatos'] })
    qc.invalidateQueries({ queryKey: ['transacoes'] })
    qc.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const aoErro = (e: Error) => toast.error(e.message)

  const enviarHtml = useMutation({
    mutationFn: (html: string) => notasFiscaisApi.importarHtml({ chave: nota.chave!, urlConsulta: nota.urlConsulta, html }),
    onSuccess: (n) => { depois(); n.status === StatusNota.Completa ? toast.success('Itens importados.') : toast.error('Não consegui ler os itens dessa página. Salve a Consulta Detalhada (Ctrl+S, "somente HTML").') },
    onError: aoErro,
  })
  const vincular = useMutation({ mutationFn: (transacaoId: string) => notasFiscaisApi.vincular(nota.id, transacaoId), onSuccess: () => { depois(); toast.success('Nota vinculada ao lançamento.') }, onError: aoErro })
  const desvincular = useMutation({ mutationFn: () => notasFiscaisApi.desvincular(nota.id), onSuccess: () => { depois(); toast.success('Vínculo desfeito.') }, onError: aoErro })
  const salvarApelido = useMutation({
    mutationFn: () => notasFiscaisApi.definirApelido(nota.cnpjEmitente!, apelido.trim() || null),
    onSuccess: () => { depois(); toast.success('Apelido salvo.') },
    onError: aoErro,
  })
  const criarLancamento = useMutation({
    mutationFn: () => notasFiscaisApi.criarLancamento(nota.id, dividindo
      ? {
        categoriaId: categoriaId || undefined, descricao: nota.apelido ?? nota.nomeEmitente ?? undefined,
        partes: partes.map((p, i) => {
          const c = cartoesUtilizaveis.find((x) => x.id === p.cartaoId)
          const parcelasParte = c?.aceitaParcelas ? Math.min(24, Math.max(1, Math.floor(Number(p.numParcelas)) || 1)) : 1
          return { cartaoId: p.cartaoId, valor: valoresDasPartes(nota.valorTotal, partes)[i], numParcelas: parcelasParte }
        }),
      }
      : pagoCom === 'cartao'
      ? { cartaoId, debito: cartaoComDebito && debito, categoriaId: categoriaId || undefined, descricao: nota.apelido ?? nota.nomeEmitente ?? undefined }
      : { accountId: contaId, categoriaId: categoriaId || undefined, descricao: nota.apelido ?? nota.nomeEmitente ?? undefined }),
    onSuccess: () => {
      depois(); qc.invalidateQueries({ queryKey: ['cartoes-utilizaveis'] }); setCriando(false); setDividir(false); setPartes(partesVazias())
      toast.success(dividindo ? 'Compra dividida lançada e vinculada.' : 'Lançamento criado e vinculado.')
    },
    onError: aoErro,
  })

  async function aoEscolherHtml(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0]
    if (!arquivo) return
    enviarHtml.mutate(await arquivo.text())
    e.target.value = ''
  }

  const { data: catalogo } = useCatalogo()
  const [editandoItem, setEditandoItem] = useState<NotaItem | null>(null)
  const [abaNota, setAbaNota] = useState<'itens' | 'insights'>('itens')

  const termo = busca.trim().toLowerCase()
  const itens = nota.itens.filter((i) => !termo || i.descricao.toLowerCase().includes(termo))

  return (
    <div className="space-y-4">
      {/* Fica no topo: o cartão só aparece quando a nota parece abastecimento */}
      {!pendente && <AbastecimentoDaNota notaId={nota.id} somenteLeitura={!!nota.ownerNome} />}
      <div className="grid gap-3 sm:grid-cols-3">
        <div><p className="text-2xs uppercase text-muted">Data</p><p className="text-13 text-white">{pendente ? `Mês ${formatDate(nota.dataEmissao).slice(3)}` : formatDate(nota.dataEmissao)}</p></div>
        <div><p className="text-2xs uppercase text-muted">Total pago</p><p className="text-13 font-semibold text-white">{pendente ? '—' : formatMoney(nota.valorTotal)}</p></div>
        <div><p className="text-2xs uppercase text-muted">Origem</p><p className="text-13 text-white">{ORIGEM_NOTA_LABEL[nota.origem]}</p></div>
      </div>
      {!pendente && nota.valorItens != null && (
        <p className="text-13 text-muted">Itens {formatMoney(nota.valorItens)}{desconto > 0.004 && <> · desconto <span className="text-income">{formatMoney(desconto)}</span></>} · total pago {formatMoney(nota.valorTotal)}</p>
      )}
      {nota.cnpjEmitente && (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1"><Input label={`Apelido da loja (CNPJ ${nota.cnpjEmitente})`} value={apelido} onChange={(e) => setApelido(e.target.value)} placeholder={nota.nomeEmitente ?? 'Ex.: Alvorada'} maxLength={80} /></div>
          <Button size="sm" variant="light" loading={salvarApelido.isPending} onClick={() => salvarApelido.mutate()}>Salvar apelido</Button>
        </div>
      )}
      {nota.chave && (
        <div>
          <p className="text-2xs uppercase text-muted">Chave de acesso</p>
          <p className="break-all font-mono text-13 text-white">{formatarChave(nota.chave)}</p>
        </div>
      )}

      {pendente && (
        <div className="space-y-2 rounded bg-surface2 p-3">
          <p className="text-13 font-medium text-white">Itens ainda não consultados</p>
          <p className="text-13 text-muted">
            A SEFAZ mostra os itens por 180 dias após a emissão (até {formatDate(limite.toISOString())} ou mais). No computador, abra a nota no site da SEFAZ,
            resolva o captcha, abra a Consulta Detalhada, salve a página (Ctrl+S, "somente HTML") e envie o arquivo aqui. No app Walleon para Android isso é automático.
          </p>
          <input ref={inputHtml} type="file" accept=".html,.htm,text/html" className="hidden" onChange={aoEscolherHtml} />
          <Button icon="ri-upload-2-line" loading={enviarHtml.isPending} onClick={() => inputHtml.current?.click()}>Enviar página salva (.html)</Button>
        </div>
      )}

      {!pendente && nota.transacaoId && (
        <div data-tutorial="nota.vinculo" className="flex flex-wrap items-center justify-between gap-2 rounded bg-surface2 p-3">
          <p className="text-13 text-white">Lançamento vinculado: <span className="text-muted">{nota.transacaoDescricao ?? 'Despesa'}</span></p>
          <Button size="sm" variant="light" loading={desvincular.isPending} onClick={() => desvincular.mutate()}>Desvincular</Button>
        </div>
      )}

      {semLancamento && (
        <div data-tutorial="nota.vinculo" className="space-y-2 rounded bg-surface2 p-3">
          <p className="text-13 font-medium text-white">Gasto desta compra</p>
          {(candidatos.data ?? []).length > 0 ? (
            <>
              <p className="text-13 text-muted">Achei lançamentos com o mesmo valor e data próxima. Vincule em vez de criar outro:</p>
              {candidatos.data!.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 text-13">
                  <span className="min-w-0 truncate text-white">{c.descricao}{c.divisaoId && <> <Badge variant="info">dividida em cartões</Badge></>} <span className="text-muted">· {formatDate(c.data)} · {c.cartao ?? c.conta ?? ''}{c.parcelas ? ` · ${c.parcelas}x` : ''} · {formatMoney(c.valor)}</span></span>
                  <Button size="sm" variant="light" loading={vincular.isPending} onClick={() => vincular.mutate(c.id)}>Vincular</Button>
                </div>
              ))}
            </>
          ) : (
            <p className="text-13 text-muted">Nenhum lançamento parecido por perto. Se o gasto ainda não está lançado, crie um.</p>
          )}
          {!criando ? (
            <Button size="sm" variant="ghost" onClick={() => setCriando(true)}>Criar lançamento de {formatMoney(nota.valorTotal)}</Button>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Select label="Pago com" value={pagoCom} onChange={(e) => setPagoCom(e.target.value as 'conta' | 'cartao')}>
                <option value="conta">Conta</option><option value="cartao">Cartão</option>
              </Select>
              {pagoCom === 'conta' ? (
                <Select label="Conta" value={contaId} onChange={(e) => setContaId(e.target.value)}>
                  <option value="">Escolha a conta</option>
                  {contas.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              ) : (
                dividir ? <p className="self-end pb-2 text-13 text-muted">Informe os cartões abaixo.</p> : (
                  <Select label="Cartão" value={cartaoId} onChange={(e) => { setCartaoId(e.target.value); setDebito(false) }}>
                    <option value="">Escolha o cartão</option>
                    {cartoes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </Select>
                )
              )}
              {pagoCom === 'cartao' && (
                <label className="sm:col-span-2 flex cursor-pointer items-center gap-2 text-13 text-white">
                  <input type="checkbox" checked={dividir} onChange={(e) => setDividir(e.target.checked)} />
                  Dividir em cartões
                </label>
              )}
              {dividindo && (
                <div data-tutorial="nota.dividir" className="sm:col-span-2">
                  <PartesDivisao valorTotal={nota.valorTotal} partes={partes} cartoes={cartoesUtilizaveis} onChange={setPartes} />
                  {erroDivisao && <p className="mt-1 text-13 text-expense">{erroDivisao}</p>}
                </div>
              )}
              {!dividindo && cartaoComDebito && (
                <Select label="Como pagou" value={debito ? 'debito' : 'credito'} onChange={(e) => setDebito(e.target.value === 'debito')}>
                  <option value="credito">Crédito (entra na fatura)</option><option value="debito">Débito (sai da conta na hora)</option>
                </Select>
              )}
              {!dividindo && creditoParcelavel && <Input label="Parcelas" type="number" min={1} max={24} value={parcelas} onChange={(e) => setParcelas(e.target.value)} />}
              {parcelado && <p className="sm:col-span-2 text-13 font-medium text-expense">Compra parcelada não se lança pela nota. Lance a compra no cartão em Novo lançamento (com as parcelas) e depois vincule a nota por lá.</p>}
              <Select label="Categoria (opcional)" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
                <option value="">Sem categoria</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
              </Select>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <Button size="sm" variant="light" onClick={() => setCriando(false)}>Cancelar</Button>
                <Button size="sm" disabled={pagoCom === 'conta' ? !contaId : dividindo ? !!erroDivisao : !cartaoId || parcelado} loading={criarLancamento.isPending} onClick={() => criarLancamento.mutate()}>Criar lançamento</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!pendente && (
        nota.itens.length === 0 ? (
          <p className="rounded bg-surface2 px-3 py-3 text-13 text-muted">Esta nota não tem itens cadastrados.</p>
        ) : (
          <>
            <Tabs tabs={[{ key: 'itens' as const, label: 'Itens', icon: 'ri-list-check-2' }, { key: 'insights' as const, label: 'Insights', icon: 'ri-lightbulb-flash-line' }]} active={abaNota} onChange={setAbaNota} />
            {abaNota === 'insights' ? <InsightsNota notaId={nota.id} somenteLeitura={!!nota.ownerNome} /> : (<>
            <Input data-tutorial="nota.itens" label="Buscar item" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Ex.: leite" />
            <Table>
              <THead><tr><Th>Item</Th><Th>Categoria</Th><Th className="text-right">Qtd</Th><Th className="text-right">Unitário</Th><Th className="text-right">Total</Th></tr></THead>
              <tbody>
                {itens.map((item) => (
                  <Tr key={item.id}>
                    <Td className="text-white">{item.descricao}</Td>
                    <Td>
                      {nota.ownerNome ? <span className="text-13 text-muted">{nomeDe(catalogo, item.categoria)}</span> : (
                        <button type="button" className="text-left text-13 text-primary hover:underline" onClick={() => setEditandoItem(item)}>
                          {nomeDe(catalogo, item.categoria)} <i className="ri-edit-line" aria-hidden />
                        </button>
                      )}
                      {item.coberturaMeses ? <span className="block text-2xs text-muted">cobre {item.coberturaMeses} meses</span> : null}
                    </Td>
                    <Td className="text-right text-muted">{item.quantidade}{item.unidade ? ` ${item.unidade}` : ''}</Td>
                    <Td className="text-right text-muted">{formatMoney(item.valorUnitario)}</Td>
                    <Td className="text-right font-medium text-white">{formatMoney(item.valorTotal)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            </>)}
          </>
        )
      )}
      {editandoItem && <ModalItem item={editandoItem} catalogo={catalogo} onClose={() => setEditandoItem(null)} />}
    </div>
  )
}

function corVariacao(v: number) {
  return v > 0 ? 'text-expense' : v < 0 ? 'text-income' : 'text-muted'
}

function useCatalogo() {
  return useQuery({ queryKey: ['itens-catalogo'], queryFn: () => notasFiscaisApi.catalogoItens(), staleTime: Infinity })
}

function nomeDe(catalogo: CatalogoItens | undefined, codigo?: string) {
  return catalogo?.categorias.find((c) => c.codigo === codigo)?.nome ?? 'Outros'
}

const ESCOPOS = [
  { valor: 'todas' as const, rotulo: 'Todas', ajuda: 'Vale para esta descrição em todas as notas, as já importadas e as futuras. Itens que você já corrigiu "somente esta" ficam como estão.' },
  { valor: 'daqui_pra_frente' as const, rotulo: 'Daqui pra frente', ajuda: 'Vale para esta nota e para as notas importadas depois de agora. As notas antigas ficam como estavam.' },
  { valor: 'somente_esta' as const, rotulo: 'Somente esta', ajuda: 'Muda só este item desta nota, sem criar regra.' },
]

/** Corrige a categoria de um item (escolhendo o alcance) e informa a cobertura em meses da compra. */
function ModalItem({ item, catalogo, onClose }: { item: NotaItem; catalogo?: CatalogoItens; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [categoria, setCategoria] = useState(item.categoria ?? 'outros')
  const [escopo, setEscopo] = useState<'todas' | 'daqui_pra_frente' | 'somente_esta'>('todas')
  const [meses, setMeses] = useState(String(item.coberturaMeses ?? 1))

  const depois = () => { qc.invalidateQueries({ queryKey: ['notas-fiscais'] }); qc.invalidateQueries({ queryKey: ['itens-comprados'] }); qc.invalidateQueries({ queryKey: ['itens-resumo'] }) }
  const salvarCategoria = useMutation({
    mutationFn: () => notasFiscaisApi.corrigirCategoriaItem(item.id, categoria, escopo),
    onSuccess: () => { depois(); toast.success('Categoria atualizada.') },
    onError: (e: Error) => toast.error(e.message),
  })
  const n = Math.floor(Number(meses))
  const salvarCobertura = useMutation({
    mutationFn: () => notasFiscaisApi.definirCoberturaItem(item.id, n),
    onSuccess: () => { depois(); toast.success('Cobertura atualizada.') },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <Modal open onClose={onClose} title="Ajustar item" footer={<Button variant="light" onClick={onClose}>Fechar</Button>}>
      <div className="space-y-4">
        <p className="text-13 text-muted">{item.descricao}</p>
        <Select label="Categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          {(catalogo?.grupos ?? []).map((g) => (
            <optgroup key={g.codigo} label={g.nome}>
              {catalogo!.categorias.filter((c) => c.grupo === g.codigo).map((c) => <option key={c.codigo} value={c.codigo}>{c.nome}</option>)}
            </optgroup>
          ))}
        </Select>
        <div>
          <p className="mb-1 text-13 font-medium text-white">Vale para</p>
          <div className="flex flex-wrap gap-2">
            {ESCOPOS.map((e) => (
              <Button key={e.valor} size="sm" variant={escopo === e.valor ? 'primary' : 'light'} onClick={() => setEscopo(e.valor)}>{e.rotulo}</Button>
            ))}
          </div>
          <p className="mt-1 text-12 text-muted">{ESCOPOS.find((e) => e.valor === escopo)?.ajuda}</p>
        </div>
        <Button loading={salvarCategoria.isPending} onClick={() => salvarCategoria.mutate()}>Salvar categoria</Button>

        <div className="space-y-2 border-t border-border/70 pt-4">
          <p className="text-13 font-medium text-white">Quantos meses esta compra cobre?</p>
          <p className="text-12 text-muted">Se comprou para vários meses (uma promoção, por exemplo), informe quantos. O consumo mensal passa a distribuir a compra por esses meses.</p>
          <div className="flex items-end gap-2">
            <div className="w-32"><Input label="Meses (1 a 24)" type="number" min={1} max={24} value={meses} onChange={(e) => setMeses(e.target.value)} /></div>
            <Button variant="light" disabled={!(n >= 1 && n <= 24)} loading={salvarCobertura.isPending} onClick={() => salvarCobertura.mutate()}>Salvar cobertura</Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/** Histórico de preço por item: busca por texto, descrições idênticas agrupadas, período livre De/Até, filtro por categoria. */
function ItensComprados({ onAbrirNota }: { onAbrirNota: (id: string) => void }) {
  const { data: catalogo } = useCatalogo()
  const [busca, setBusca] = useState('')
  const [termo, setTermo] = useState('')
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [categoria, setCategoria] = useState('')
  const [recorrencia, setRecorrencia] = useState('')
  const [contarEstoque, setContarEstoque] = useState<{ g: ItemCompradoGrupo; itemId: string } | null>(null)
  const [planoDe, setPlanoDe] = useState<ItemCompradoGrupo | null>(null)
  const [aberto, setAberto] = useState<string | null>(null)
  const [editando, setEditando] = useState<NotaItem | null>(null)
  const qc = useQueryClient()
  const marcar = useMutation({
    mutationFn: (v: { itemId: string; tipo: TipoMarcaRecorrencia }) => notasFiscaisApi.marcarRecorrenciaItem(v.itemId, v.tipo),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['itens-comprados'] }),
  })

  useEffect(() => {
    const t = setTimeout(() => setTermo(busca.trim()), 400)
    return () => clearTimeout(t)
  }, [busca])

  const { data: grupos = [], isFetching, isError } = useQuery({
    queryKey: ['itens-comprados', termo, de, ate, categoria, recorrencia],
    queryFn: () => notasFiscaisApi.itensComprados({ busca: termo, dataInicio: de || undefined, dataFim: ate || undefined, categoria: categoria || undefined, recorrencia: (recorrencia || undefined) as 'mensal' | 'anual' | 'avulso' | undefined }),
    // sem texto, o filtro de categoria ou de tipo já basta para listar
    enabled: termo.length >= 2 || !!categoria || !!recorrencia,
  })

  return (
    <Card>
      <CardHeader title="Itens comprados" icon="ri-price-tag-3-line" />
      <div className="space-y-4 p-5">
        <p className="text-13 text-muted">Busque um item e veja o preço em cada compra. A mesma descrição (e unidade) é agrupada; descrições diferentes aparecem como itens separados. Cada compra é uma nota: linhas repetidas na mesma nota contam como uma compra, e a quantidade soma.</p>
        <p className="text-2xs text-muted">{AJUDA_RECORRENCIA}</p>
        <div className="grid gap-3 sm:grid-cols-5">
          <Input label="Buscar item" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Ex.: leite" />
          <Input label="De" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
          <Input label="Até" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
          <Select label="Categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Todas</option>
            {(catalogo?.grupos ?? []).map((g) => (
              <optgroup key={g.codigo} label={g.nome}>
                <option value={g.codigo}>Grupo: {g.nome}</option>
                {catalogo!.categorias.filter((c) => c.grupo === g.codigo).map((c) => <option key={c.codigo} value={c.codigo}>{c.nome}</option>)}
              </optgroup>
            ))}
          </Select>
          <Select label="Tipo" value={recorrencia} onChange={(e) => setRecorrencia(e.target.value)}>
            <option value="">Todos</option><option value="mensal">Mensal</option><option value="anual">Anual</option><option value="avulso">Avulso</option>
          </Select>
        </div>
        {termo.length < 2 && !categoria && !recorrencia ? (
          <p className="text-13 text-muted">Digite pelo menos 2 letras, ou escolha uma categoria ou um tipo (Mensal, Anual, Avulso).</p>
        ) : isFetching ? (
          <LoadingState />
        ) : isError ? (
          <p className="text-13 text-expense">Não consegui buscar. Tente de novo.</p>
        ) : grupos.length === 0 ? (
          <p className="text-13 text-muted">Nenhum item encontrado nas notas importadas.</p>
        ) : (
          <div className="space-y-2">
            {grupos.map((g: ItemCompradoGrupo) => {
              const chave = `${g.descricao}|${g.unidade}`
              const aberta = aberto === chave
              const maisRecente = g.ocorrencias[0]
              return (
                <div key={chave} className="rounded border border-border/70">
                  <button type="button" className="flex w-full items-center justify-between gap-3 p-3 text-left" onClick={() => setAberto(aberta ? null : chave)}>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-white">{g.descricao}</p>
                      <p className="text-2xs text-info">{g.categoriaNome} · <span className={g.recorrencia === 'avulso' ? 'text-muted' : 'text-primary'}>{g.recorrencia === 'mensal' ? 'Mensal' : g.recorrencia === 'anual' ? 'Anual' : 'Avulso'}{g.recorrenciaOrigem === 'manual' ? ' (marcado por você)' : ''}</span></p>
                      <p className="text-2xs text-muted">{g.compras} compra(s) · {g.quantidadeTotal} {g.unidade} no total · gasto {formatMoney(g.gastoTotal)} · menor {formatMoney(g.menorPreco)} · maior {formatMoney(g.maiorPreco)} · {formatMoney(g.precoPadrao)}/{g.unidadePadrao.toLowerCase()}</p>
                      {g.recorrencia !== 'avulso' && g.valorEsperadoMensal > 0 && (
                        <p className="text-2xs text-muted">Esperado por mês: {g.valorEsperadoMensal} {g.unidadePadrao.toLowerCase()}{g.proximaCompraPrevista ? ` · próxima compra prevista para ${formatDate(g.proximaCompraPrevista)}` : ''}</p>
                      )}
                      {g.principioAtivo && <p className="text-2xs text-muted">Princípio ativo: {g.principioAtivo}{g.grupo === 'medicamentos' && g.recorrencia === 'mensal' ? ' · uso contínuo (comprado todo mês)' : ''}</p>}
                      {g.estoqueAtual != null && (
                        <p className="text-2xs text-muted">Estoque estimado: {g.estoqueAtual} {g.unidadePadrao.toLowerCase()}{g.estoqueAcabaEm ? ` · acaba por volta de ${formatDate(g.estoqueAcabaEm)}` : ' · acabou'}{g.estoqueContado ? ' (a partir da sua contagem)' : ''}</p>
                      )}
                      {g.reposicaoAtrasada && <p className="text-2xs font-semibold text-warning">Já deveria ter acabado: está na hora de repor?</p>}
                      {g.variacaoVsMediana != null && Math.abs(g.variacaoVsMediana) >= 5 && (
                        <p className={cn('text-2xs font-semibold', corVariacao(g.variacaoVsMediana))}>
                          {g.variacaoVsMediana > 0 ? 'Preço acima' : 'Preço abaixo'} da mediana das compras anteriores ({g.variacaoVsMediana > 0 ? '+' : ''}{g.variacaoVsMediana.toFixed(0)}%)
                        </p>
                      )}
                      {g.sugestaoCobertura && <p className="text-2xs font-semibold text-primary">A última compra foi bem maior que a habitual: cobre quantos meses? (sugestão: {g.sugestaoCobertura})</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-white">{formatMoney(g.ultimoPreco)}</p>
                      {g.compras > 1 && <p className={cn('text-2xs font-semibold', corVariacao(g.variacaoPercentual))}>{g.variacaoPercentual > 0 ? '+' : ''}{g.variacaoPercentual.toFixed(1)}%</p>}
                    </div>
                  </button>
                  {aberta && (
                    <div className="space-y-1 border-t border-border/70 p-3">
                      {g.ocorrencias.map((o, i) => {
                        const anterior = g.ocorrencias[i + 1]
                        const delta = anterior ? o.valorUnitario - anterior.valorUnitario : 0
                        return (
                          <button key={`${o.notaId}-${i}`} type="button" className="flex w-full items-center justify-between gap-3 text-13 hover:text-white" onClick={() => onAbrirNota(o.notaId)}>
                            <span className="text-muted">{formatDate(o.data)} · {o.loja} · {o.quantidade} {g.unidade}</span>
                            <span className="font-medium text-white">
                              {formatMoney(o.valorUnitario)}
                              {anterior && delta !== 0 && <span className={cn('ml-2 text-2xs', delta > 0 ? 'text-expense' : 'text-income')}>{delta > 0 ? '▲' : '▼'} {formatMoney(Math.abs(delta))}</span>}
                            </span>
                          </button>
                        )
                      })}
                      {g.recorrenciaMotivo && <p className="text-2xs text-muted">{g.recorrenciaMotivo}</p>}
                      {maisRecente?.itemId && (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <span className="text-2xs text-muted">Marcar como:</span>
                          {(['mensal', 'anual', 'avulso', 'automatico'] as TipoMarcaRecorrencia[]).map((t) => {
                            const atual = g.recorrenciaOrigem === 'manual' ? g.recorrencia : 'automatico'
                            return <Button key={t} size="sm" variant={atual === t ? 'primary' : 'light'} onClick={() => marcar.mutate({ itemId: maisRecente.itemId!, tipo: t })}>{t === 'automatico' ? 'Automático' : t[0].toUpperCase() + t.slice(1)}</Button>
                          })}
                        </div>
                      )}
                      {maisRecente?.itemId && (
                        <Button size="sm" variant="light" onClick={() => setEditando({ id: maisRecente.itemId!, descricao: g.descricao, quantidade: 0, valorUnitario: 0, valorTotal: 0, categoria: g.categoria })}>
                          Ajustar categoria e cobertura
                        </Button>
                      )}
                      {maisRecente?.itemId && g.recorrencia !== 'avulso' && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button size="sm" variant="light" onClick={() => setContarEstoque({ g, itemId: maisRecente.itemId! })}>Contar estoque</Button>
                          <Button size="sm" variant="light" onClick={() => setPlanoDe(g)}>Planejar compra grande</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {editando && <ModalItem item={editando} catalogo={catalogo} onClose={() => setEditando(null)} />}
      {contarEstoque && <EstoqueModal g={contarEstoque.g} itemId={contarEstoque.itemId} onClose={() => setContarEstoque(null)} />}
      {planoDe && <PlanoCompraModal g={planoDe} onClose={() => setPlanoDe(null)} />}
    </Card>
  )
}

const AGRUPAR = [
  { key: 'dia' as const, label: 'Dia' }, { key: 'semana' as const, label: 'Semana' },
  { key: 'mes' as const, label: 'Mês' }, { key: 'ano' as const, label: 'Ano' },
]

/** Resumo do período: o período (De/Até) escolhe as notas; "Agrupar por" divide o resultado no tempo. */
function ResumoItensWeb() {
  const [de, setDe] = useState('')
  const [ate, setAte] = useState('')
  const [agrupar, setAgrupar] = useState<'dia' | 'semana' | 'mes' | 'ano'>('mes')
  const { data: catalogo } = useCatalogo()

  const { data: r, isFetching, isError } = useQuery({
    queryKey: ['itens-resumo', de, ate, agrupar],
    queryFn: () => notasFiscaisApi.resumoItens({ dataInicio: de || undefined, dataFim: ate || undefined, agruparPor: agrupar }),
  })
  const maxSerie = Math.max(1, ...(r?.serie.map((x) => x.gasto) ?? [1]))

  const lista = (itens: ResumoCategoria[]) => itens.map((c) => (
    <div key={c.codigo} className="space-y-1">
      <div className="flex justify-between text-13"><span className="text-white">{c.nome}</span><span className="text-muted">{formatMoney(c.gasto)} · {c.percentual.toFixed(0)}%</span></div>
      <div className="h-1.5 overflow-hidden rounded bg-surface2"><div className="h-full rounded bg-primary" style={{ width: `${Math.min(100, Math.max(2, c.percentual))}%` }} /></div>
    </div>
  ))

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader title="Resumo dos itens" icon="ri-bar-chart-2-line" />
        <div className="space-y-3 p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <Input label="De" type="date" value={de} onChange={(e) => setDe(e.target.value)} />
            <Input label="Até" type="date" value={ate} onChange={(e) => setAte(e.target.value)} />
            <div>
              <p className="mb-1 text-13 text-muted">Agrupar por</p>
              <div className="flex gap-2">{AGRUPAR.map((a) => <Button key={a.key} size="sm" variant={agrupar === a.key ? 'primary' : 'light'} onClick={() => setAgrupar(a.key)}>{a.label}</Button>)}</div>
            </div>
          </div>
          {isFetching ? <LoadingState /> : isError ? <p className="text-13 text-expense">Não consegui carregar o resumo.</p> : !r || r.totalItens === 0 ? (
            <p className="text-13 text-muted">Nenhum item nas notas importadas neste período.</p>
          ) : (
            <div>
              <p className="text-2xs uppercase text-muted">Gasto em itens (soma de todas as notas do período)</p>
              <p className="text-24 font-bold text-white">{formatMoney(r.totalGasto)}</p>
              <p className="text-13 text-muted">{r.totalNotas} nota(s) · {r.totalItens} linha(s) de item. Os preços são os de tabela, antes do desconto da nota.</p>
            </div>
          )}
        </div>
      </Card>

      {r && r.totalItens > 0 && !isFetching && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card><CardHeader title="Por grupo" /><div className="space-y-3 p-5">{lista(r.porGrupo)}{r.itensSaudeOcultos > 0 && <p className="text-12 text-muted">{r.itensSaudeOcultos} item(ns) de saúde de outra pessoa não aparecem, só o total.</p>}</div></Card>
          <Card><CardHeader title="Por categoria" /><div className="space-y-3 p-5">{lista(r.porCategoria)}</div></Card>
          <Card>
            <CardHeader title={`No tempo (${AGRUPAR.find((a) => a.key === agrupar)?.label.toLowerCase()})`} />
            <div className="space-y-3 p-5">
              {r.serie.map((x) => (
                <div key={x.inicio} className="space-y-1">
                  <div className="flex justify-between text-13"><span className="text-white">{x.rotulo}</span><span className="text-muted">{formatMoney(x.gasto)} · {x.notas} nota(s)</span></div>
                  <div className="h-1.5 overflow-hidden rounded bg-surface2"><div className="h-full rounded bg-info" style={{ width: `${(x.gasto / maxSerie) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <CardHeader title="Por loja" />
            <div className="space-y-2 p-5">
              {r.porLoja.map((l) => <div key={l.loja} className="flex justify-between gap-3 text-13"><span className="truncate text-white">{l.loja}</span><span className="text-muted">{formatMoney(l.gasto)} · {l.notas} nota(s) · ticket {formatMoney(l.ticketMedio)}</span></div>)}
            </div>
          </Card>
          <Card>
            <CardHeader title="O que mais pesa" />
            <div className="space-y-2 p-5">
              {r.quemMaisPesa.map((i) => <div key={i.descricao} className="flex justify-between gap-3 text-13"><span className="min-w-0 truncate text-white">{i.descricao} <span className="text-2xs text-info">{nomeDe(catalogo, i.categoria)}</span></span><span className="text-muted">{formatMoney(i.gasto)} · {i.percentual.toFixed(1)}%</span></div>)}
            </div>
          </Card>
          <Card>
            <CardHeader title="Variação dos seus preços" />
            <div className="space-y-1 p-5">
              {r.inflacao.percentual != null ? (
                <>
                  <p className={cn('text-24 font-bold', r.inflacao.percentual > 0 ? 'text-expense' : 'text-income')}>{r.inflacao.percentual > 0 ? '+' : ''}{r.inflacao.percentual.toFixed(1)}%</p>
                  <p className="text-13 text-muted">Média geométrica da variação do preço por kg, litro ou unidade de {r.inflacao.itensComparaveis} itens comprados em meses diferentes (o primeiro contra o último preço de cada um).</p>
                </>
              ) : <p className="text-13 text-muted">{r.inflacao.aviso ?? 'Sem dados suficientes ainda.'}</p>}
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
